import { useQuery } from "@tanstack/react-query";
import { apiFetch } from "./apiClient";

/**
 * KB CAAS Authorization Workflows — interaktivní multi-step feature
 * (`/api/v1/kb-caas/**`, API chain, CSRF OFF, admin JWT cookie). 5 workflow
 * operací (activate-by-kbk / login / ros / authorize-transaction / sessions) +
 * session continuation (NEEDS_OTP / NEEDS_OIDC_REDIRECT). Sdílená envelope
 * `KbCaasAuthChainResponse` (status diskriminátor). Vlastní fetch (envelope má
 * smysl i u non-2xx). Port kbcaasAuth sekce z legacy kb-caas.html.
 */

export type KbCaasStatus = "COMPLETED" | "NEEDS_OTP" | "NEEDS_OIDC_REDIRECT" | "IN_PROGRESS" | "BLOCKED" | "FAILED" | "CANCELLED";

export interface KbCaasNextAction {
  what?: string;
  endpoint?: string;
  payloadSchema?: Record<string, unknown>;
  retriesLeft?: number;
  expiresAt?: string;
  pollAfterSeconds?: number;
  hint?: string;
}

export interface KbCaasError {
  code?: string;
  message?: string;
  canRetry?: boolean;
  retriesLeft?: number;
  recoveryHint?: string;
}

export interface KbCaasEnvelope {
  status: KbCaasStatus;
  sessionId?: string;
  result?: unknown;
  nextAction?: KbCaasNextAction;
  error?: KbCaasError;
  chainSnapshot?: unknown;
}

export interface KbCaasRosResult {
  requestUri?: string;
  kbSessionId?: string;
  exp?: number;
  iss?: string;
  aud?: string;
}

export interface KbCaasSessionSummary {
  sessionId: string;
  workflow: string;
  status: KbCaasStatus;
  lastTemplate?: string;
  createdAt?: string;
  lastStepAt?: string;
  finishedAt?: string;
  note?: string;
}

const BASE = "/api/v1/kb-caas";

async function kbCaasFetch<T>(path: string, method: string, body?: unknown, extraHeaders?: Record<string, string>): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    method,
    credentials: "include",
    headers: {
      Accept: "application/json",
      ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
      ...extraHeaders,
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  const text = await res.text();
  if (!text) return {} as T;
  try {
    return JSON.parse(text) as T;
  } catch {
    return { status: "FAILED", error: { message: text || `HTTP ${res.status}` } } as unknown as T;
  }
}

export function kbCaasActivateByKbk(body: Record<string, unknown>): Promise<KbCaasEnvelope> {
  return kbCaasFetch<KbCaasEnvelope>("/activate-by-kbk", "POST", body);
}

export function kbCaasLogin(body: Record<string, unknown>): Promise<KbCaasEnvelope> {
  return kbCaasFetch<KbCaasEnvelope>("/login", "POST", body);
}

export function kbCaasRegisterRos(body: Record<string, unknown>): Promise<KbCaasRosResult & KbCaasEnvelope> {
  return kbCaasFetch<KbCaasRosResult & KbCaasEnvelope>("/ros", "POST", body);
}

export function kbCaasAuthorizeTransaction(body: Record<string, unknown>, kbSessionId: string): Promise<KbCaasEnvelope> {
  return kbCaasFetch<KbCaasEnvelope>("/authorize-transaction", "POST", body, { "X-etts-X-KB-Session-Id": kbSessionId });
}

export function kbCaasContinue(sessionId: string, body: Record<string, unknown>): Promise<KbCaasEnvelope> {
  return kbCaasFetch<KbCaasEnvelope>(`/sessions/${encodeURIComponent(sessionId)}/continue`, "POST", body);
}

export function kbCaasCancel(sessionId: string): Promise<KbCaasEnvelope> {
  return kbCaasFetch<KbCaasEnvelope>(`/sessions/${encodeURIComponent(sessionId)}/cancel`, "POST", { statusDetail: "USER_CANCELLED" });
}

/**
 * Našeptávač tokenů pro workflow „aktivace přes KB+": aktivní KB CAAS tokeny typu
 * STANDALONE (KB Klíč). Omezení dělá **server** — dřív se stahoval celý seznam tokenů
 * a průnik se seznamem STANDALONE se skládal tady; nad stránkovaným zdrojem by takový
 * průnik tiše zahazoval řádky ze stránky.
 */
export function searchKbCaasStandaloneTokens(query: string, limit = 20) {
  const p = new URLSearchParams({ size: String(limit) });
  if (query.trim()) p.set("q", query.trim());
  return apiFetch<{ tokenId: string; username: string; state: string | null }[]>(
    `/admin/api/v1/tools/api/kb-caas/tokens?${p}`,
  );
}

export function useKbCaasSessions(enabled: boolean) {
  return useQuery({
    queryKey: ["kb-caas-sessions"],
    queryFn: () => kbCaasFetch<KbCaasSessionSummary[]>("/sessions", "GET"),
    enabled,
    refetchOnWindowFocus: false,
  });
}

/** Výchozí msgToAuth.data (NPPU platba) pro ROS formulář — 1:1 s legacy prefill. */
export const DEFAULT_MSG_TO_AUTH_DATA = `{
  "msgData": {
    "paymentId": "I123456789",
    "counterPartyIdentification": "Alza",
    "instructedAmount": "200",
    "currency": "CZK",
    "variableSymbol": "452",
    "constantSymbol": "321",
    "specificSymbol": "4569257",
    "initiatorName": "Alza",
    "initiatorBankCode": "3010",
    "initiatorAccount": "246-246589",
    "counterPartyBankCode": "3000",
    "payeeName": "televize",
    "messageForCounterParty": "Ahoj",
    "ownDescription": "platba za TV",
    "originatorName": "Komercni banka",
    "applicationName": "platebni brana kb",
    "effectiveDate": "1.7.2021"
  },
  "msgType": {
    "spec": "NPPU",
    "timestamp": "2021-06-08T12:24:55Z",
    "reason": 1,
    "version": 1
  },
  "msgDataExtended": {
    "notifications": [
      { "ntfSendCond": "Pri zauctovani", "ntfChannel": "SMS", "ntfAddress": "Adresa", "ntfLanguage": "Cesky" }
    ]
  }
}`;
